'use client'
import { ErrorMsg } from '@/components/error-msg'
import { useErrorToast } from '@/components/toastHook'
import { trackEvent } from '@/lib/analytics'
import InputField from '@a24/form/InputField'
import Button from '@a24/ui/Button'
import Icon from '@a24/ui/Icon'
import Link from '@a24/ui/Link'
import Popover from '@a24/ui/Popover'
import Title from '@a24/ui/Title'
import { startTransition, useActionState, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import handleSubmit, { type State } from './action'
import SignUpForm from './signup-form'

interface ILoginFormData {
  userid: string
  password: string
}
const initialState = null
const LoginForm = () => {
  const formMethods = useForm<ILoginFormData>()
  const [state, formAction, isPending] = useActionState<State, FormData>(
    handleSubmit,
    initialState
  )
  useErrorToast(state)
  const formRef = useRef<HTMLFormElement>(null)
  return (
    <div>
      <FormProvider {...formMethods}>
        <form
          ref={formRef}
          id='login-form'
          className=''
          onSubmit={(evt) => {
            evt.preventDefault()
            formMethods.handleSubmit(() => {
              // @ts-ignore
              // biome-ignore lint/style/noNonNullAssertion: <explanation>
              startTransition(() => formAction(new FormData(formRef.current!)))
            })(evt)
          }}
        >
          <div className='grid md:flex gap-6'>
            <InputField
              outerWrapperClassName='w-full label-md'
              placeholder='j.doe@mailto.com'
              name='userid'
              label='Email'
              type='text'
              rules={{
                maxLength: 40,
                required: 'Email is required'
              }}
            />
            <InputField
              outerWrapperClassName='w-full label-md'
              placeholder='******'
              name='password'
              label='Password'
              type='password'
              rules={{
                maxLength: 30,
                required: 'Password is required'
              }}
            />
          </div>
          {state && <ErrorMsg state={state} />}
        </form>
      </FormProvider>

      <div className='flex flex-col-reverse md:flex-row md:justify-between'>
        <div>
          <Link
            href='https://tickets.cherrylanetheatre.org/Online/forgotPassword.asp'
            className='link mt-10 text-center md:text-left md:mt-5 w-full justify-center'
          >
            Forgot password
          </Link>
        </div>
        <div className='flex flex-col sm:flex-row justify-end'>
          <Popover
            className='max-h-[95dvh] min-w-[85vw] top-[50%] translate-y-[-50%] border border-heritage-black-full backdrop:bg-heritage-black-full/20 rounded bg-heritage-white-full left-[50%] translate-x-[-50%]'
            closeButtonContent={
              <Icon width={24} height={24} id='close-menu-icon' name='close' />
            }
            closeButtonProps={{
              type: 'button',
              className: 'absolute top-0 right-0 z-20 cursor-pointer p-6'
            }}
            buttonProps={{
              disabled: isPending,
              'aria-disabled': isPending,
              type: 'button',
              className: 'w-full sm:w-auto button-tertiary mt-5 mr-4',
              onClick: () =>
                trackEvent({
                  name: 'button_interaction',
                  data: { value: 'CreateAccount|Cancel' }
                })
            }}
            buttonContent='Create an account'
          >
            <div className='p-6 relative max-w-4xl mx-auto'>
              <Title
                level='h2'
                className='text-center heading-md pb-6 md:pt-20'
                text='Create an account'
              />
              <SignUpForm redirect={false} />
            </div>
          </Popover>
          <Button
            disabled={isPending}
            aria-disabled={isPending}
            type='submit'
            form='login-form'
            className='w-full sm:w-auto button-secondary mt-5'
            onClick={() =>
              trackEvent({
                name: 'button_interaction',
                data: { value: 'Login|Submit' }
              })
            }
          >
            Login
          </Button>
        </div>
      </div>
    </div>
  )
}

export default LoginForm
